/**
 * 後台主選單（17 群組，繁中詞條與舊選單一字不差）。
 *
 * 來源：legacy/Ccheng_web/docs/_generated/menu.json（權威樹狀來源，含每個葉節點的
 * permission 字串）＋ 舊站 https://cchengroad.playworld.com.tw/admin 側邊欄實測
 * DOM 文字（Playwright 擷取，詞條逐字保留，含編號/符號/全形括號）。
 * href 由該 feature 的 folder（docs/by-feature/_manifest.json）去掉 "admin/" 前綴
 * 而來，對應規格 admin 分頁路徑；本批（B1 admin 殼接通）尚未實作對應頁面，
 * 一律先落到 [...slug] 建置中佔位頁（製作順序規劃.md §4 B1 列）。
 *
 * 第 1 輪嚴重修正（任務指示第 2、5、6 條 + 業主裁決 6-1）：
 * - 群組順序還原為舊站 17 群組原始順序；群組標題圈碼前綴逐字重打（➊~➓、➊➊~➊➐）。
 * - 「人才提報」12 個子項目還原為數字 1..12 順序。
 *
 * 第 2 輪嚴重修正（任務指示第 1~5、7~11 條；權威來源 menu.json + 舊站側邊欄實測）：
 * - g2 聯誼會：子項還原為 1.方案條件設定 → 2.名單 → 3.收據（原本 1、2 對調）。
 * - g8 文件/員工代碼/心成幣：補回漏掉的第 4 項「4. 操作歷程 ☞」
 *   （legacy route=/audit-trail/default/index；manifest 無對應 feature folder，
 *   href 依 B1 慣例落在 backend-administration 群組下，先導到 [...slug] 佔位頁）。
 * - g10 權限管理/黑名單：還原舊站兩層結構——「3. 後端-權限管理」「4. 前端-權限管理」
 *   為不可點擊的子群組標題（NavItem 無 href + children，Sidebar 已支援巢狀渲染），
 *   其下各自收 4 項/3 項，不再拍平成 9 個無從分辨的重複項目。
 * - g11 小天使專區：8 個子項改回舊站「設定→回傳」逐階段交錯順序，且 label 補回
 *   開頭漏掉的「⟪」（舊站實測為「⟪第一次關懷⟫ 設定」等，前後全形括號成對）。
 * - g15 配置：6 個子項還原為編號 1→6 順序；「全局配置／系統版本／清除緩存(快取)」
 *   三筆原誤指向 /broadcast-management/line-configuration（複製貼上錯誤），改依
 *   g15 現有慣例統一至 /configurations/*（slug 取 legacy route /configuration/
 *   {site,system,cache} 尾段）。
 * - 全部 17 群組的每個葉節點補上 menu.json 的 permission 字串（任務指示第 6、14 條；
 *   legacy left.php 對每一項都做 $identity->can(...)，新站 filterNavByPermissions
 *   須能對全選單生效，而非僅 4 個群組）。
 * key 維持原值（g0~g16 僅為 React key／[...slug] 反查用，與顯示順序無關）。
 *
 * 第 3 輪嚴重修正（任務指示第 1 條）：4 組「多個選單項目共用同一 href」的路由撞死——
 * legacy 以同一 route 加 query 參數區分頁面（member/genealogy、member/team、
 * angel-care ?type=1..4、configuration/{line,sms}），原 href 只取 feature folder
 * 導致 9 個子項與同組第一項完全同址，[...slug] 佔位頁反查出多筆詞條串接顯示。
 * 依裁決比照 legacy 語意補上區分：
 * - g1-i1 學員關係圖 → ?view=genealogy；g2-i0 聯誼會名單 → ?view=team
 *   （legacy route /admin/member/{genealogy,team}）。
 * - g11 小天使 8 項：第一次關懷為 legacy 預設 type=1（href 不變），第二/第三/後續
 *   關懷之設定與回傳各補 ?type=2/3/4（legacy /admin/angel-care-setting?type=N、
 *   /admin/angel-care-data?type=N）。
 * - g14-i8 簡訊配置 → 獨立 slug /broadcast-management/sms-configuration
 *   （legacy route /admin/configuration/sms；比照 g15 第 2 輪修正「slug 取 legacy
 *   route /configuration/{site,system,cache} 尾段」的既有慣例，不再與 LINE 配置同址）。
 * [...slug] 佔位頁同步改為以「路徑＋query」精確比對單一詞條（見該檔）。
 */

import type { NavSpec } from "@ccheng/ui";

export const adminNav: NavSpec = [
  {
    key: "g0",
    icon: "🌐",
    label: "➊ 網站管理",
    items: [
      {
        key: "g0-i0",
        label: "學長故事",
        href: "/web-management/students-sharing",
        permission: "/admin/students-sharing/index",
      },
      {
        key: "g0-i1",
        label: "最新消息",
        href: "/web-management/latest-news",
        permission: "/admin/latest-news/index",
      },
      {
        key: "g0-i2",
        label: "活動剪影",
        href: "/web-management/video-reels",
        permission: "/admin/video-reels/index",
      },
      {
        key: "g0-i3",
        label: "心成月刊",
        href: "/web-management/monthly-newsletter",
        permission: "/admin/monthly-newsletter/index",
      },
      {
        key: "g0-i4",
        label: "輪播廣告",
        href: "/web-management/carousel",
        permission: "/admin/carousel/index",
      },
      {
        key: "g0-i5",
        label: "區塊/聯誼會制度",
        href: "/web-management/block",
        permission: "/admin/block/index",
      },
      {
        key: "g0-i6",
        label: "網頁",
        href: "/web-management/page",
        permission: "/admin/page/index",
      },
      {
        key: "g0-i7",
        label: "常見問題",
        href: "/web-management/faq",
        permission: "/admin/faq/index",
      },
    ],
  },
  {
    key: "g1",
    icon: "👥",
    label: "➋ 會員/關係圖/訪談員⭐",
    items: [
      {
        key: "g1-i0",
        label: "1. 會員/學員資料",
        href: "/member-management/user-profile",
        permission: "/admin/member/index",
      },
      {
        key: "g1-i1",
        label: "2. 學員關係圖",
        href: "/member-management/user-profile?view=genealogy",
        permission: "/admin/member/genealogy",
      },
      {
        key: "g1-i2",
        label: "3. 解盤回報",
        href: "/member-management/session-report",
        permission: "/admin/review/index",
      },
      {
        key: "g1-i3",
        label: "4. 訪談員代碼",
        href: "/member-management/interviewer-id",
        permission: "/admin/interviews/index",
      },
    ],
  },
  {
    key: "g2",
    icon: "🤝",
    label: "➌ 聯誼會 ❤️",
    items: [
      {
        key: "g2-i1",
        label: "1. 聯誼會方案、條件設定",
        href: "/society-management/social-group",
        permission: "/admin/subscription/index",
      },
      {
        key: "g2-i0",
        label: "2. 聯誼會名單",
        href: "/member-management/user-profile?view=team",
        permission: "/admin/member/team",
      },
      {
        key: "g2-i2",
        label: "3. 聯誼會收據",
        href: "/society-management/pdf-template",
        permission: "/admin/pdf-template/index",
      },
    ],
  },
  {
    key: "g3",
    icon: "💌",
    label: "➍ 心之使者 ❤️",
    items: [
      {
        key: "g3-i0",
        label: "1. 心之使者條件設定",
        href: "/heart-messenger-management/heart-messenger-condition",
        permission: "/admin/heart-messenger-condition/index",
      },
      {
        key: "g3-i1",
        label: "2. 心之使者名單",
        href: "/heart-messenger-management/heart-messenger-list",
        permission: "/admin/heart-messenger/index",
      },
    ],
  },
  {
    key: "g4",
    icon: "📚",
    label: "➎ 課程管理♠︎ / 簽到 ✎",
    items: [
      {
        key: "g4-i0",
        label: "1. 建立/查詢課程",
        href: "/course-management/training-program",
        permission: "/admin/course/index",
      },
      {
        key: "g4-i1",
        label: "2. 簽到簿 ✎",
        href: "/course-management/session-attendance",
        permission: "/admin/attendance/index",
      },
      {
        key: "g4-i2",
        label: "3. 大堂課 梯次狀況",
        href: "/course-management/class-enrollment-status",
        permission: "/admin/course-registration-status/index",
      },
      {
        key: "g4-i3",
        label: "4. 課程標籤",
        href: "/course-management/course-label-setting",
        permission: "/admin/course-label-condition/index",
      },
    ],
  },
  {
    key: "g6",
    icon: "🏛️",
    label: "➏ 大堂課操作‼️",
    items: [
      {
        key: "g6-i0",
        label: "1. 大堂課key序號",
        href: "/course-operation/key-in",
        permission: "/admin/course-event-day/index",
      },
      {
        key: "g6-i1",
        label: "2. 上傳組別、上傳房號",
        href: "/course-operation/grouping-tool",
        permission: "/admin/course-grouping/index",
      },
      {
        key: "g6-i2",
        label: "3. 房間分配工具",
        href: "/course-operation/room-assignment-tool",
        permission: "/admin/room-assignment-tool/index",
      },
      {
        key: "g6-i3",
        label: "4. 列印現金袋、名牌",
        href: "/course-operation/course-overview",
        permission: "/admin/course-grouping-summary/index",
      },
      {
        key: "g6-i4",
        label: "5. 展示梯次 https://cchengroad.com/countdown-dashboard",
        href: "/course-operation/course-dashboard",
        permission: "/admin/course-dashboard/index",
      },
      {
        key: "g6-i5",
        label: "6. 家長報名率",
        href: "/course-operation/parent-attendance",
        permission: "/admin/parent-attendance/index",
      },
      {
        key: "g6-i6",
        label: "5. 大堂課小組名單",
        href: "/course-operation/group-assignment",
        permission: "/admin/group-assignment/index",
      },
      {
        key: "g6-i7",
        label: "8. 大堂課中離",
        href: "/course-operation/course-departure",
        permission: "/admin/course-departure/index",
      },
    ],
  },
  {
    key: "g7",
    icon: "⚙️",
    label: "➐ 課程後端設定",
    items: [
      {
        key: "g7-i0",
        label: "1. 前台課程介紹",
        href: "/course-setting/course-info",
        permission: "/admin/course-group/index",
      },
      {
        key: "g7-i1",
        label: "2. 新增-課程項目",
        href: "/course-setting/course-module",
        permission: "/admin/course-type/index",
      },
      {
        key: "g7-i2",
        label: "3. 新增-課程類別",
        href: "/course-setting/course-level",
        permission: "/admin/course-category/index",
      },
      {
        key: "g7-i3",
        label: "課程區域",
        href: "/course-setting/region",
        permission: "/admin/region/index",
      },
    ],
  },
  {
    key: "g8",
    icon: "📄",
    label: "➑ 文件/員工代碼/心成幣 ⇄",
    items: [
      {
        key: "g8-i0",
        label: "1. 文件下載",
        href: "/backend-administration/backend-documents",
        permission: "/admin/documents/index",
      },
      {
        key: "g8-i1",
        label: "2. 員工代碼",
        href: "/backend-administration/backend-voucher-management",
        permission: "/admin/voucher/index",
      },
      {
        key: "g8-i2",
        label: "3. 心成幣 ⇄",
        href: "/backend-administration/backend-credits",
        permission: "/admin/credits/index",
      },
      {
        key: "g8-i3",
        label: "4. 操作歷程 ☞",
        href: "/backend-administration/audit-trail",
        permission: "/admin/audit-trail/default/index",
      },
    ],
  },
  {
    key: "g9",
    icon: "🚗",
    label: "➒ 車馬費$/體系管理",
    items: [
      {
        key: "g9-i0",
        label: "1. 體系管理",
        href: "/payout/network-system",
        permission: "/admin/network-system/index",
      },
      {
        key: "g9-i1",
        label: "2. 模板建立",
        href: "/payout/payout-version",
        permission: "/admin/payout-version/index",
      },
      {
        key: "g9-i2",
        label: "3. 上傳車馬費",
        href: "/payout/payout-upload",
        permission: "/admin/payout-upload/index",
      },
      {
        key: "g9-i3",
        label: "4. 大體系管理",
        href: "/payout/backend-network-system-group",
        permission: "/admin/network-system-group/index",
      },
    ],
  },
  {
    key: "g10",
    icon: "🔐",
    label: "➓ 權限管理/黑名單 ❌",
    items: [
      {
        key: "g10-i0",
        label: "1. 區域行政(簽到掃描)",
        href: "/special-assignment/local-administration-assignment",
        permission: "/admin/local-administration-assignment/index",
      },
      {
        key: "g10-i1",
        label: "2. 黑名單 ❌",
        href: "/special-assignment/backend-black-list",
        permission: "/admin/black-list/index",
      },
      {
        // 舊站為不可點擊的子群組標題（javascript:void(0)），其下收 4 個子項；
        // 無 href → Sidebar 渲染為展開/收合按鈕。可見性由 children 過濾結果決定
        // （比照 legacy left.php：任一子項有權限即顯示標題）。
        key: "g10-i9",
        label: "3. 後端-權限管理",
        children: [
          {
            key: "g10-i2",
            label: "權限列表",
            href: "/special-assignment/permission-list",
            permission: "/admin/permission/index",
          },
          {
            key: "g10-i3",
            label: "權限群組",
            href: "/special-assignment/permission-group-list",
            permission: "/admin/permission-group/index",
          },
          {
            key: "g10-i4",
            label: "角色列表",
            href: "/special-assignment/role-list",
            permission: "/admin/role/index",
          },
          {
            key: "g10-i5",
            label: "用户列表",
            href: "/special-assignment/user-list",
            permission: "/admin/user/index",
          },
        ],
      },
      {
        key: "g10-i10",
        label: "4. 前端-權限管理",
        children: [
          {
            key: "g10-i6",
            label: "權限列表",
            href: "/special-assignment/permission-list-permissionfrontend",
            permission: "/admin/permission-frontend/index",
          },
          {
            key: "g10-i7",
            label: "權限群組",
            href: "/special-assignment/permission-group-list-permissiongroupfrontend",
            permission: "/admin/permission-group-frontend/index",
          },
          {
            key: "g10-i8",
            label: "角色列表",
            href: "/special-assignment/role-list-rolefrontend",
            permission: "/admin/role-frontend/index",
          },
        ],
      },
    ],
  },
  {
    key: "g11",
    icon: "😇",
    label: "➊➊ 小天使專區",
    items: [
      {
        key: "g11-i0",
        label: "⟪第一次關懷⟫ 設定",
        href: "/angel-care-zone/first-care-setting",
        permission: "/admin/angel-care-setting/index",
      },
      {
        key: "g11-i4",
        label: "⟪第一次關懷⟫ －回傳",
        href: "/angel-care-zone/first-care-data",
        permission: "/admin/angel-care-data/index",
      },
      {
        key: "g11-i1",
        label: "⟪第二次關懷⟫ 設定",
        href: "/angel-care-zone/first-care-setting?type=2",
        permission: "/admin/angel-care-setting/index",
      },
      {
        key: "g11-i5",
        label: "⟪第二次關懷⟫ －回傳",
        href: "/angel-care-zone/first-care-data?type=2",
        permission: "/admin/angel-care-data/index",
      },
      {
        key: "g11-i2",
        label: "⟪第三次關懷⟫ 設定",
        href: "/angel-care-zone/first-care-setting?type=3",
        permission: "/admin/angel-care-setting/index",
      },
      {
        key: "g11-i6",
        label: "⟪第三次關懷⟫ －回傳",
        href: "/angel-care-zone/first-care-data?type=3",
        permission: "/admin/angel-care-data/index",
      },
      {
        key: "g11-i3",
        label: "⟪後續關懷⟫ 設定",
        href: "/angel-care-zone/first-care-setting?type=4",
        permission: "/admin/angel-care-setting/index",
      },
      {
        key: "g11-i7",
        label: "⟪後續關懷⟫ －回傳",
        href: "/angel-care-zone/first-care-data?type=4",
        permission: "/admin/angel-care-data/index",
      },
    ],
  },
  {
    key: "g5",
    icon: "🌟",
    label: "➊➋ 人才提報 ✿",
    items: [
      {
        key: "g5-i1",
        label: "1. 提報審核",
        href: "/backend-talent-referral/reserved-talent-review",
        permission: "/admin/reserved-talent-recommendation/index",
      },
      {
        key: "g5-i2",
        label: "2. 人才列表 8,11,15,18點更新",
        href: "/backend-talent-referral/talent-list",
        permission: "/admin/talent-list/index",
      },
      {
        key: "g5-i3",
        label: "3. 預備人才",
        href: "/backend-talent-referral/backend-reserved-talent",
        permission: "/admin/reserved-talent/index",
      },
      {
        key: "g5-i4",
        label: "4. 提報 條件設定",
        href: "/backend-talent-referral/recommend-conditions",
        permission: "/admin/reserved-talent-recommend-condition/index",
      },
      {
        key: "g5-i5",
        label: "5. 續聘考核 條件設定",
        href: "/backend-talent-referral/renewal-assessment-conditions",
        permission: "/admin/renewal-assessment-condition/index",
      },
      {
        // 2026-09-05 更正（既有編號 #48）：原 href 與 g4-i0「1. 建立/查詢課程」
        // 共用 `/course-management/training-program`（B3 建課清單），但兩者是
        // 不同 controller action（`admin/course/index` vs `admin/course/
        // talent-frontend`，見 舊系統權限交叉表.json 與 b9_plan.md §4.2 #48）。
        // 拆開後兩個節點才各自可達；本頁尚未落地，依既有慣例（同批其餘 11 個
        // g5 項目）落 `[...slug]` 佔位頁，不預先建目錄。
        key: "g5-i0",
        label: "6. 預備人才順序設定 ⇵",
        href: "/backend-talent-referral/reserved-talent-sort",
        permission: "/admin/course/talent-frontend",
      },
      {
        key: "g5-i6",
        label: "7. ⟪傳愛領袖聲明⟫ 設定",
        href: "/backend-talent-referral/declaration-setting",
        permission: "/admin/talent-declaration-setting/index",
      },
      {
        key: "g5-i7",
        label: "8. ⟪關懷員聲明⟫ 設定",
        href: "/backend-talent-referral/taishin-talent-declaration-setting",
        permission: "/admin/taishin-talent-declaration-setting/index",
      },
      {
        key: "g5-i8",
        label: "9. 傳愛領袖聲明－回傳",
        href: "/backend-talent-referral/talent-declaration-data",
        permission: "/admin/talent-declaration-data/index",
      },
      {
        key: "g5-i9",
        label: "10. 關懷員聲明－回傳",
        href: "/backend-talent-referral/taishin-talent-declaration-data",
        permission: "/admin/taishin-talent-declaration-data/index",
      },
      {
        key: "g5-i10",
        label: "11. 提報表模板",
        href: "/backend-talent-referral/talent-referral-form-template",
        permission: "/admin/talent-referral-form-template/index",
      },
      {
        key: "g5-i11",
        label: "12. 人才列表年底存檔",
        href: "/backend-talent-referral/talent-list-archive",
        permission: "/admin/talent-list-archive/index",
      },
    ],
  },
  {
    key: "g12",
    icon: "🎬",
    label: "➊➌ 內訓影片 ▷",
    items: [
      {
        key: "g12-i0",
        label: "1. 影片類別",
        href: "/training-video-management/training-video-category",
        permission: "/admin/training-video-category/index",
      },
      {
        key: "g12-i1",
        label: "2. 影片資料夾",
        href: "/training-video-management/training-video-folder",
        permission: "/admin/training-video-folder/index",
      },
      {
        key: "g12-i2",
        label: "3. 影片清單",
        href: "/training-video-management/training-video",
        permission: "/admin/training-video/index",
      },
    ],
  },
  {
    key: "g13",
    icon: "💰",
    label: "➊➍ 財務訂單/轉讓/退款‼️",
    items: [
      {
        key: "g13-i0",
        label: "1. 訂單",
        href: "/finance/order",
        permission: "/admin/order/index",
      },
      {
        key: "g13-i1",
        label: "2. 轉讓 ➦",
        href: "/finance/transfer",
        permission: "/admin/transfer/index",
      },
      {
        key: "g13-i2",
        label: "3. 退款作業 ✄",
        href: "/finance/refund",
        permission: "/admin/refund/index",
      },
      {
        key: "g13-i3",
        label: "4. 下載聯誼會收據(Zip)",
        href: "/finance/society-receipt-zip",
        permission: "/admin/society-receipt-batch/index",
      },
    ],
  },
  {
    key: "g14",
    icon: "📢",
    label: "➊➎ 推播管理",
    items: [
      {
        key: "g14-i0",
        label: "1. 推播",
        href: "/broadcast-management/backend-broadcast",
        permission: "/admin/broadcast/index",
      },
      {
        key: "g14-i1",
        label: "2. Email模板",
        href: "/broadcast-management/email-template",
        permission: "/admin/email-template/index",
      },
      {
        key: "g14-i2",
        label: "3. LINE模板",
        href: "/broadcast-management/line-template",
        permission: "/admin/line-template/index",
      },
      {
        key: "g14-i3",
        label: "4. 簡訊模板",
        href: "/broadcast-management/sms-template",
        permission: "/admin/sms-template/index",
      },
      {
        key: "g14-i4",
        label: "5. Email 發送紀錄 ➢",
        href: "/broadcast-management/email-log",
        permission: "/admin/email-log/index",
      },
      {
        key: "g14-i5",
        label: "6. LINE 發送紀錄 ➢",
        href: "/broadcast-management/line-log",
        permission: "/admin/line-log/index",
      },
      {
        key: "g14-i6",
        label: "7. SMS 發送紀錄 ➢",
        href: "/broadcast-management/sms-log",
        permission: "/admin/sms-log/index",
      },
      {
        key: "g14-i7",
        label: "8. LINE配置 ◎",
        href: "/broadcast-management/line-configuration",
        permission: "/admin/configuration/line",
      },
      {
        key: "g14-i8",
        label: "9. 簡訊配置 ◎",
        href: "/broadcast-management/sms-configuration",
        permission: "/admin/configuration/sms",
      },
    ],
  },
  {
    key: "g15",
    icon: "🔧",
    label: "➊➏ 配置",
    items: [
      {
        key: "g15-i0",
        label: "1. 全局配置",
        href: "/configurations/site",
        permission: "/admin/configuration/site",
      },
      {
        key: "g15-i3",
        label: "2. 付款管理",
        href: "/configurations/payment",
        permission: "/admin/payment-settings/index",
      },
      {
        key: "g15-i4",
        label: "3. 翻譯",
        href: "/configurations/translation",
        permission: "/admin/translation/index",
      },
      {
        key: "g15-i1",
        label: "4. 系統版本",
        href: "/configurations/system",
        permission: "/admin/configuration/system",
      },
      {
        key: "g15-i5",
        label: "5. 國家設定",
        href: "/configurations/manage-country",
        permission: "/admin/countries/index",
      },
      {
        key: "g15-i2",
        label: "6. 清除緩存(快取)",
        href: "/configurations/cache",
        permission: "/admin/configuration/cache",
      },
    ],
  },
  {
    key: "g16",
    icon: "📊",
    label: "➊➐ 統計圖表",
    items: [
      {
        key: "g16-i0",
        label: "1. 傳愛人數",
        href: "/backend-statistics/backend-refer-count-report",
        permission: "/admin/refer-count-report/index",
      },
    ],
  },
];

/** 扁平化列表：[...slug] 佔位頁用 href 反查選單詞條顯示（含巢狀 children）。 */
export const adminNavFlat: { key: string; label: string; href: string; group: string }[] =
  adminNav.flatMap((group) =>
    (group.items ?? []).flatMap(function flatten(item): {
      key: string;
      label: string;
      href: string;
      group: string;
    }[] {
      const self = item.href
        ? [{ key: item.key, label: item.label, href: item.href, group: group.label ?? "" }]
        : [];
      return [...self, ...(item.children ?? []).flatMap(flatten)];
    }),
  );
